/**
 * How a date is written for a reader.
 *
 * Day / month / year, Gregorian, with Western digits — the form the school
 * writes on paper. Storage stays ISO (`YYYY-MM-DD`), which sorts and compares
 * correctly; this is presentation only, and the two must not be confused.
 *
 * `Intl.DateTimeFormat` is deliberately not used. Under `ar` it reaches for the
 * Islamic calendar and Arabic-Indic digits depending on the runtime's locale
 * data, so the same date renders differently on two machines — and a birth date
 * that changes shape between the employee's screen and the committee's is a
 * support call.
 */

const SEPARATOR = '/';

/**
 * Formats an ISO date (or the date part of an ISO timestamp).
 *
 * Anything that is not one is returned unchanged: a screen showing a raw value
 * is a visible bug, while a screen showing an empty box hides it.
 */
export function formatDateAr(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!match) return iso;

  const [, year, month, day] = match;
  return [day, month, year].join(SEPARATOR);
}

/** The reverse, for anywhere a person types the readable form. */
export function parseDateAr(value: string): string | null {
  const match = /^(\d{1,2})\s*\/\s*(\d{1,2})\s*\/\s*(\d{4})$/.exec(value.trim());
  if (!match) return null;

  const [, day, month, year] = match;
  if (!day || !month || !year) return null;

  const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

  // Round-trips through Date to reject 31/02, which the pattern alone accepts.
  // The NaN guard is not defensive padding: a month of 29 — what "07/29/2026"
  // parses to — makes `toISOString` throw rather than return a wrong answer.
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toISOString().slice(0, 10) === iso ? iso : null;
}
